import React, { useState } from "react";
import PropTypes from "prop-types";
import ShareIcon from "../../../ui/icons/ShareIcon";
import Dropdown from "../../../ui/Dropdown";
import Button from "../../../ui/Button";
import Accordion from "../../../ui/Accordion";
import Alert from "../Alert";
import { createUserPatientShare } from "../../CreateTPlan_1Upload/helpers";
import { acquirePlanLock } from "../../../helpers/backend_api";
import useAutoDismissMessage from "../../../hooks/useAutoDismissMessage";
import {
    clientErrorMessages,
    PATIENT_ALREADY_SHARED,
    SUCCESS,
} from "../../../constants";
import {
    SHARE_AND_NOTES_HEADERS,
    SHARE_AND_NOTES_PLACEHOLDERS,
    SHARE_AND_NOTES_SUBHEADER,
    TIME_OUT_DURATION_MS,
} from "./constants";
import CheckCircleIcon from "../../../ui/icons/CheckCircleIcon";
import { addPendoTracking } from "../../../helpers/helpers";
import { trackingEventNames } from "../../../helpers/pendo_constants";

function SharePatientCard(props) {
    const [selectedDoctorUUID, setSelectedDoctorUUID] = useState(undefined);
    const [shareSuccessful, setShareSuccessful] = useState(false);
    const [shareAlertMsg, setShareAlertMsg] =
        useAutoDismissMessage(TIME_OUT_DURATION_MS);

    function handleSelectDoctor(selectedDoctor) {
        setSelectedDoctorUUID(selectedDoctor.uuid);
    }

    async function handleSharePatient() {
        try {
            const response = await createUserPatientShare(
                props.authToken,
                selectedDoctorUUID,
                props.patientUUID,
                props.planUUID
            );

            if (response.status === SUCCESS) {
                setShareSuccessful(true);
                await acquirePlanLock({
                    token: props.authToken,
                    useruuid: props.userUUID,
                    planuuid: props.planUUID,
                });
                addPendoTracking(
                    trackingEventNames.SHARE_PATIENT,
                    props.planUUID,
                    null,
                    { sharedDoctorUUID: selectedDoctorUUID }
                );
            } else if (response.status === PATIENT_ALREADY_SHARED) {
                setShareSuccessful(false);
                setShareAlertMsg(clientErrorMessages.PATIENT_ALREADY_SHARED);
            } else {
                setShareSuccessful(false);
                setShareAlertMsg(clientErrorMessages.FAILED_TO_SHARE_PATIENT);
            }
        } catch (error) {
            console.error("Error sharing patient:", error);
        }
    }

    return (
        <Accordion
            headerChildren={
                <>
                    <div className="flex items-center gap-2">
                        <ShareIcon />
                        <div className="text-xl">
                            {SHARE_AND_NOTES_HEADERS.SHARE_PATIENT}
                        </div>
                    </div>
                    <div className="relative mt-2 text-left text-sm">
                        {shareAlertMsg && (
                            // TODO: Implement our own Alert component and use ID instead of CLASSNAME for testing
                            <Alert
                                className="patient-share-failure-alert absolute top-0 z-10 w-full items-center p-0.5 text-center text-sm"
                                variant="danger"
                                text={shareAlertMsg}
                            />
                        )}
                        {SHARE_AND_NOTES_SUBHEADER}
                    </div>
                </>
            }
            contentChildren={
                <div className="mt-2 flex flex-col gap-2">
                    <Dropdown
                        id="share-patient"
                        placeholder={
                            SHARE_AND_NOTES_PLACEHOLDERS.SHARE_PATIENT_DROPDOWN
                        }
                        onChange={handleSelectDoctor}
                        dropdownItems={props.doctorList.map((doctorData) => ({
                            label: `${doctorData.firstName} ${doctorData.lastName}`,
                            ...doctorData,
                        }))}
                        renderDropdownItem={(item) => {
                            return (
                                <>
                                    <div>
                                        Dr. {item.firstName} {item.lastName}
                                    </div>
                                    <div className="truncate">{item.email}</div>
                                </>
                            );
                        }}
                    />
                    <div className="relative flex items-center justify-center px-16">
                        <Button
                            id="patient-share"
                            onClick={handleSharePatient}
                            text={
                                SHARE_AND_NOTES_PLACEHOLDERS.SHARE_PATIENT_BTN
                            }
                            disabled={!selectedDoctorUUID}
                            variant="primary"
                        />
                        {shareSuccessful && (
                            <CheckCircleIcon
                                id="patient-share-success"
                                className="absolute right-20"
                            />
                        )}
                    </div>
                </div>
            }
            defaultOpen={true}
        />
    );
}

SharePatientCard.propTypes = {
    authToken: PropTypes.string,
    userUUID: PropTypes.string,
    patientUUID: PropTypes.string,
    planUUID: PropTypes.string,
    doctorList: PropTypes.array,
};

export default SharePatientCard;
